import axios from "axios";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../../contexts/AuthContext";
import AppContext from "../../../contexts/AppContext";
import { currencyFormat } from "../../../utils/currencyFormat";
import filterFunctions from "../../../utils/filterFunctions";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import SalesSubscriptionDetails from "../../../features/Sales/SalesSubscriptions/SalesSubscriptionDetails";
import NewSalesPaymentsForm from "../../../features/Sales/SalesPayments/NewSalesPaymentsForm/NewSalesPaymentsForm";
import showNotification from "../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../utils/error-handler";

const FORM_CONFIG = {
   "CREATE": "CREATE",
   "EDIT": "EDIT",
   "NONE": "NONE",
}


export function useSalesSubscriptions() {
   const [subscriptionData, setSubscriptionData] = useState(null);
   const [displayForm, setDisplayForm] = useState(FORM_CONFIG.NONE);
   const [initialValuesEdit, setInitialValuesEdit] = useState(null);
   const [modalOpen, setModalOpen] = useState(false);
   const [studentData, setStudentData] = useState();
   const [loading, setLoading] = useState(false);
   const [updateData, setUpdateData] = useState(false);
   const { bankAccountsOptions } = useContext(AppContext);
   const { authToken } = useContext(AuthContext)

   const editSubscription = (rowData) => {
      if (rowData) {
         setInitialValuesEdit({
            rowData,
            _id: rowData._id,
            gateway_id: rowData.gateway_id,
            source: rowData.source,
            inscriptionOrigin: rowData.inscriptionOrigin || "GENERAL",
            dollar_day: rowData.payment.dollar_day,
            isNewCustomer: rowData.payer.isNewCustomer ? "true" : "false",
            sellerId: rowData.sellerId,
            bankTransferAccount:
               rowData.payment.bankTransferAccount ||
               bankAccountsOptions[bankAccountsOptions.length - 1].value,
            type: rowData.type,
            commit: rowData.commit,
            agreedAmount: rowData.agreedAmount,
            installments: {
               total_cycles: rowData.installments[0]?.total_cycles,
               first_due_date:
                  moment(rowData.installments[0]?.date).toDate() || null,
               data:
                  rowData.installments.length > 0
                     ? rowData.installments.map((_inst) => {
                        return {
                           _id: _inst._id,
                           due_date: moment(_inst.date).toDate(),
                           amount: _inst.amount, // Or agreedAmount
                           currency: _inst.currency,
                           state: _inst.state
                        };
                     })
                     : null,
            },
            currency: rowData.payment.currency,
            trackingCode: rowData.tracking_code, // Tracking code to fetch the receipt
            receipt: rowData.receipt, // Initiates on null to force fetch on form
            discount: rowData.discount._id === "0" ? null : rowData.discount._id,
            discountId: rowData.discount._id === "0" ? null : rowData.discount._id,
            keepInstallmentsOnARS: rowData.payment.keepInstallmentsOnARS || false,
            hasAdvancedConfigEnabled: rowData.installments.length > 0 || false,
         });
         setDisplayForm(true);
      }
   };


   const handleSetInitialValuesCreate = () => {
      setInitialValuesEdit({
         alreadyUser: false,
         currency: "ARS",
         discount: null
      })
   }

   const getFormAndPreview = (type, form) => {
      const isForm = type === "FORM";
      const isCreate = displayForm === FORM_CONFIG.CREATE;

      if (isForm && isCreate) {
         return <NewSalesPaymentsForm isSubscription form={form} loading={loading} />
      } else if (isForm) {
         return <SalesSubscriptionDetails form={form} setUpdateData={setUpdateData} />
      }
      return <></>
   };

   
   const createSale = async (values, config) => {

       const jsonData = {
           data: {
               ...values,
           },
       };

       return axios.post(
           `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/createSale`,
           jsonData,
           config
       );
   };
   
   // const editSale = async (values, config, _uploadToS3) => {
   //     values.installments.total_cycles = parseInt(
   //         values.installments.total_cycles,
   //         10
   //     );
   //     if (values.receipt) {
   //         const clearTitle = `${values.sellerId}`.replace(/[^\w\s]/g, "").trim();
   //         values.receipt = await _uploadToS3({
   //             image: values.receipt,
   //             fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
   //             fileDirectory: "receipts",
   //             isRequired: true,
   //         });
   //     }
   
   //     const jsonData = {
   //         data: {
   //             ...values,
   //         },
   //     };
   //     return axios.put(
   //         `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/setSellers/${values._id}`,
   //         jsonData,
   //         config
   //     );
   // };

   const handleSubmit = async (values) => {
           const config = {
               headers: {
                   "Content-Type": "application/json",
                   Authorization: `Bearer ${authToken}`,
               },
           };
           try {
               setLoading(true);
               let notificationMessage = ""
   
               // Check if alreadyUser key exist to identify which form if being submitted
               if (values.hasOwnProperty("alreadyUser")) {
                   // Handle sale creation
                   await createSale({...values, source: "SUBSCRIPTION", is_active: values.is_active === 'true' ? true : false}, config);
                   notificationMessage = `Nuevo suscriptor agregado correctamente`
               } else {
                   // Handle sale edit
                  //  await editSale(values, config, uploadToS3);
                   notificationMessage = `La suscripción ha sido editada correctamente.`
               }
   
               showNotification({
                   color: "green",
                   status: "success",
                   title: "¡Excelente! Operación exitosa.",
                   message: notificationMessage,
               });
   
               setUpdateData(!updateData);
               setDisplayForm(FORM_CONFIG.NONE);
               setInitialValuesEdit(null);
           } catch (error) {
               showNotification({
                   color: "red",
                   status: "error",
                   title: "Operación fallida.",
                   message: `No se pudo crear / editar la suscripción. Detalle: ${
                       ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                   }`,
                   autoClose: 12000,
               });
           } finally {
               setLoading(false);
           }
       };

   // DATA INITIALIZATION
   const init = async () => {
      const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/paymentsTable?subscriptions=true`;
      const config = {
         headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
         },
      };
      const paymentsResponse = await axios.get(url, config);
      // Sort by date
      paymentsResponse.data.sort((a, b) => {
         return new Date(b.created_at) - new Date(a.created_at);
      });
      // Filter non-confirmed payments
      const PAYMENTS_TO_FILTER = ["payer_action_required"];
      const filteredPayments = paymentsResponse.data.filter((_pay) => {
         return !PAYMENTS_TO_FILTER.includes(_pay.state.toLowerCase());
      });
      // Also filter "APPROVAL_PENDING" payments that are not paid ("PENDING")
      // If you want to change this, mind that will have to getStateBadge function
      const filteredPayments2 = filteredPayments.filter((_pay) => {
         const firstInstallmentState = _pay.installments?.[0]?.state || "NULL";
         return !(
            _pay.state.toLowerCase() === "approval_pending" &&
            firstInstallmentState === "PENDING"
         );
      });

      setSubscriptionData(filteredPayments2);
   };

   useEffect(() => {
      init();
   }, [updateData]);

   const handleStudentData = (_studentData) => {
      setStudentData(_studentData);
      if (studentData !== undefined) {
         setModalOpen(true);
      }
   };

   const columnHelper = createColumnHelper();
   const columns = [
      columnHelper.accessor("created_at", {
         cell: (info) => {
            const dateValue = new Date(info.getValue());
            return (
               <div className="salesPayment-cell-center">
                  {moment(dateValue).format("DD/MM/YYYY")}
               </div>
            );
         },
         header: "Inscripción",
         size: 248,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "date",
         filterFn: filterFunctions.dateRangeFilter,
      }),
      columnHelper.accessor("payer", {
         cell: (info) => {
            const value = info.getValue();
            return (
               <span
                  onClick={() => handleStudentData(value)}
                  className="salesPayment-studentData-span"
               >
                  {value.name} {value.lastname}
               </span>
            );
         },
         header: "Estudiante",
         size: 200,
         filter: "text",
         enableSorting: false,
         filterFn: filterFunctions.objectFilter,
      }),
      columnHelper.accessor("installments", {
         cell: (info) => {
            const installments = info.getValue();
            if (!installments || installments.length === 0) return "-";

            const lastInstallment = [...installments].sort((a, b) =>
               moment(b.date).diff(moment(a.date))
            )[0];

            return moment(lastInstallment.date).format("DD/MM/YYYY");
         },
         header: "Ultimo pago",
         size: 304,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "date",
         filterFn: filterFunctions.dateRangeFilter,
      }),
      columnHelper.accessor("details", {
         cell: (info) => {
            const value = info.getValue();
            return value ? value : "No hay suscripción disponible";
         },
         header: "Suscripción",
         size: 304,
         filter: "text",
         enableSorting: false,
         filterFn: filterFunctions.textFilter,
      }),
      columnHelper.accessor("payment.type", {
         cell: (info) => info.getValue(),
         header: "Plataforma",
         size: 164,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "select",
         filterOptions: [
            { value: "PAYPAL", label: "PAYPAL" },
            { value: "PAGOS360", label: "PAGOS360" },
            { value: "TRANSFERENCIA", label: "TRANSFERENCIA" },
         ],
         filterFn: (row, id, filterValue) => {
            if (!filterValue) return true;
            return (
               row.original.payment.type.toLowerCase() === filterValue.toLowerCase()
            );
         },
      }),
      columnHelper.accessor("payment.bankTransferAccount", {
         cell: (info) => info.getValue(),
         header: "Cuenta",
         size: 164,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "select",
         filterOptions: bankAccountsOptions,
         filterFn: (row, id, filterValue) => {
            if (!filterValue || !row.original.payment.bankTransferAccount)
               return true;
            return (
               row.original.payment.bankTransferAccount.toLowerCase() ===
               filterValue.toLowerCase()
            );
         },
      }),
      columnHelper.accessor("agreedAmount", {
         cell: (info) => `$${currencyFormat(info.getValue())}`,
         header: "Monto",
         size: 112,
         enableColumnFilter: false,
         enableSorting: true,
         tooltip:
            "Monto abonado por el estudiante. Después de aplicar el descuento.",
      }),
      columnHelper.accessor("payment.currency", {
         cell: (info) => info.getValue(),
         header: "Moneda",
         size: 88,
         enableColumnFilter: true,
         enableSorting: false,
         filter: "select",
         filterOptions: [
            { value: "ARS", label: "ARS" },
            { value: "USD", label: "USD" },
         ],
         filterFn: (row, id, filterValue) => {
            if (!filterValue) return true;
            return (
               row.original.payment.currency.toLowerCase() ===
               filterValue.toLowerCase()
            );
         },
      }),
      columnHelper.accessor("discount.percent", {
         cell: (info) => `${info.getValue()}%`,
         header: "DCTO",
         size: 88,
         enableColumnFilter: false,
         enableSorting: true,
      }),
   ];

   return {
      displayForm,
      setDisplayForm,
      initialValuesEdit,
      setInitialValuesEdit,
      subscriptionData,
      loading,
      columns,
      modalOpen,
      setModalOpen,
      setStudentData,
      studentData,
      editSubscription,
      handleSetInitialValuesCreate,
      FORM_CONFIG,
      getFormAndPreview,
      handleSubmit,
   }
}